import type { SpaceCalendarEntry } from '@cocoonspace/sdk-js/types/spaceCalendarEntry'
import { useGetSpaceCalendar } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Separator } from '@cocoonspace/ui/components/separator'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { useLingui } from '@lingui/react/macro'

import { useSpaceVisitStepper } from '../space-visit-stepper.context'

const { getTimeAsMinutes } = dateTimeUtils.utils

const getSlotsAsAmPm = (slots: SpaceCalendarEntry[]) => {
	return slots
		.filter((slot) => slot.available)
		.reduce(
			(acc, item) => {
				const midDayAsMin = getTimeAsMinutes('12:00')
				const hourAsMin = getTimeAsMinutes(item.hour)

				if (hourAsMin < midDayAsMin) {
					acc.am.push(item)
				} else {
					acc.pm.push(item)
				}

				return acc
			},
			{ am: [], pm: [] } as {
				am: SpaceCalendarEntry[]
				pm: SpaceCalendarEntry[]
			},
		)
}

const BtnList = ({
	slots,
	isLoading,
	label,
}: {
	label: string
	slots: SpaceCalendarEntry[]
	isLoading: boolean
}) => {
	const { t } = useLingui()
	const { setStartTime, setStep } = useSpaceVisitStepper()

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center gap-2 text-sm font-bold'>
				{label} <Separator className='flex-1' />
			</div>

			<ul className='grid w-full grid-cols-4 gap-2'>
				{isLoading &&
					Array.from({ length: 8 }).map((_, i) => (
						<Skeleton
							className='h-8 w-full'
							key={i}
						/>
					))}

				{!isLoading && !slots.length && (
					<div className='flex h-10 items-center justify-center text-sm sm:col-span-2 md:col-span-4'>
						{t`Pas de créneau disponible`}
					</div>
				)}

				{!isLoading &&
					slots.map((slot) => (
						<Button
							className='hover:bg-primary hover:text-primary-foreground font-normal'
							key={slot.hour}
							onClick={() => {
								setStartTime(slot.hour)
								setStep('payment')
							}}
							size='sm'
							variant='outline'
						>
							{slot.hour}
						</Button>
					))}
			</ul>
		</div>
	)
}

export const HourStep = ({ spaceId }: { spaceId: string }) => {
	const { t } = useLingui()

	const { day } = useSpaceVisitStepper()

	const calendarQuery = useGetSpaceCalendar(
		spaceId,
		{ day, type: 'visit' },
		{ query: { select: (data) => getSlotsAsAmPm(data.data) } },
	)

	return (
		<div className='flex flex-col gap-4'>
			<BtnList
				isLoading={calendarQuery.isLoading}
				label={t`Matin`}
				slots={calendarQuery.data?.am ?? []}
			/>

			<BtnList
				isLoading={calendarQuery.isLoading}
				label={t`Après-midi`}
				slots={calendarQuery.data?.pm ?? []}
			/>
		</div>
	)
}
